import classNames from 'classnames';
import { HashLink as Link } from 'react-router-hash-link';
import ButtonLoading from '../buttonLoading/buttonLoading';
import type { IconButtonProps } from './types';
import { useIconButton } from './useIconButton';
import './iconButton.scss';

const EMPTY_STYLE = {};

/**
 * Icon-only or icon-with-text action button that can render as a router link or a native button. Reads onClick from useIconButton, derives the variant class from the filled-gradient, filled, outline and icon-with-text flags, and while isLoading replaces the icon with ButtonLoading and drops the click handler.
 *
 * @param props - Icon component, optional text and link target, variant and state flags, and styling overrides.
 * @returns The rendered link or button element.
 */
const IconButton = (props: IconButtonProps) => {
  const {
    id = undefined,
    'aria-label': ariaLabel = undefined,
    className = undefined,
    isFilledGradient = false,
    isFilled = false,
    isOutline = false,
    disabled = false,
    style = EMPTY_STYLE,
    textStyle = EMPTY_STYLE,
    iconText = '',
    isIconLinkButton = false,
    link = '',
    isIconWithText = false,
    target = undefined,
    isLoading = false,
    iconComponent,
  } = props;
  const { onClick } = useIconButton(props);

  return isIconLinkButton ? (
    <Link
      id={id}
      aria-label={ariaLabel}
      className={classNames(
        'icon-btn',
        isFilledGradient
          ? '--filled-gradient'
          : isFilled
            ? '--filled'
            : isOutline
              ? '--outline'
              : isIconWithText
                ? '--icon-with-text'
                : '--icon-only',
        className,
      )}
      style={style}
      disabled={disabled}
      to={link}
      target={target}
    >
      {iconComponent}

      {!!iconText && (
        <span className="icon-text" style={textStyle}>
          {iconText}
        </span>
      )}
    </Link>
  ) : (
    <button
      id={id}
      aria-label={ariaLabel}
      className={classNames(
        'icon-btn',
        isFilledGradient
          ? classNames('--filled-gradient', isLoading ? '--is-loading' : '')
          : isFilled
            ? '--filled'
            : isOutline
              ? '--outline'
              : isIconWithText
                ? '--icon-with-text'
                : '--icon-only',
        className,
      )}
      type="button"
      style={style}
      disabled={disabled}
      onClick={isLoading ? undefined : onClick}
    >
      {!isLoading && iconComponent}

      {!!iconText && (
        <span className="icon-text" style={textStyle}>
          {iconText}
        </span>
      )}

      {isLoading && <ButtonLoading isFilled={isFilledGradient} />}
    </button>
  );
};

export default IconButton;
